/**
 * "MANAGE ASSIGNMENTS" — the permission that decides who may reassign a rep.
 *
 * The action itself already exists in the policy matrix: `ACTIONS.CC_REP_ASSIGN`
 * ("Manage Assignments"), granted to `exec` alone. This module adds the second
 * half Brian asked for — an exec-only TOGGLE that grants or revokes it for one
 * person (or one whole role) without changing anyone's role.
 *
 * Resolution order, most specific first:
 *   1. an explicit per-user grant from the backend (`permissions` on
 *      /api/access/me or on the roster row) — the only durable source
 *   2. a local per-user override set through the toggle UI
 *   3. a local per-role override set through the toggle UI
 *   4. the policy matrix (`can(CC_REP_ASSIGN)`) — exec only
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHAT IS AND ISN'T REAL HERE.
 *
 * sr-portal-backend has no per-user permission store today, so a toggle flipped
 * in the UI is written OPTIMISTICALLY to the roster route and, when that route
 * rejects the unknown field, held in a sessionStorage override that dies with
 * the tab. The UI says which of the two happened on every row — a grant that
 * did not persist is labelled as such rather than drawn as if it had.
 *
 * And as everywhere else in this platform: this decides what gets DRAWN. Until
 * the backend authorizes `/api/champion-update` per caller, a determined user
 * can still call the route directly. See the top of lib/rbac.js, and the exact
 * backend dependency in CLAUDE.md.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { ACTIONS, ROLES, ROLE_META } from './rbac.js'

/** The permission key. One id, shared by the policy matrix and the toggle. */
export const MANAGE_ASSIGNMENTS = ACTIONS.CC_REP_ASSIGN

/** What it is called wherever a human reads it. */
export const MANAGE_ASSIGNMENTS_LABEL = 'Manage Assignments'

/** Roles the toggle may target. `unscoped` is deliberately grantable: a new
 *  hire not yet on the roster is exactly who an exec might need to switch on. */
export const TOGGLEABLE_ROLES = [ROLES.EXEC, ROLES.REP, ROLES.OPS, ROLES.MARKETING, ROLES.UNSCOPED]

export const ROLE_LABEL = (role) => ROLE_META[role]?.label || role

const STORE_KEY = 'sr_assignment_grants'

/* ---- subjects -------------------------------------------------------- */

export function userSubject(user) {
  const id = user?.id ?? user?.username ?? user?.email ?? null
  return id ? `user:${String(id).trim().toLowerCase()}` : null
}

export function roleSubject(role) {
  return role ? `role:${String(role).trim().toLowerCase()}` : null
}

/* ---- the local override store ---------------------------------------- */

export function readOverrides() {
  try {
    const raw = sessionStorage.getItem(STORE_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeOverrides(next) {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(next))
  } catch {
    /* storage unavailable — the override simply doesn't survive this call */
  }
}

/** Grant (`true`), revoke (`false`) or clear (`null`) one subject. */
export function setOverride(subject, value) {
  if (!subject) return readOverrides()
  const next = { ...readOverrides() }
  if (value === null || value === undefined) delete next[subject]
  else next[subject] = !!value
  writeOverrides(next)
  return next
}

export function clearOverrides() {
  writeOverrides({})
}

/* ---- the resolver ---------------------------------------------------- */

/**
 * Does the backend payload carry an explicit grant for this permission?
 * Tolerant of the shapes a permissions DTO usually arrives in.
 */
export function grantedByBackend(permissions) {
  if (!permissions) return false
  if (Array.isArray(permissions)) {
    return permissions.some((p) => {
      const v = typeof p === 'object' ? p?.key ?? p?.action ?? p?.name : p
      return String(v || '').trim().toLowerCase() === MANAGE_ASSIGNMENTS
    })
  }
  if (typeof permissions === 'object') {
    return !!(permissions[MANAGE_ASSIGNMENTS] || permissions.canManageAssignments)
  }
  return false
}

/**
 * The decision, with WHY — so a denied control can state its reason instead of
 * silently doing nothing.
 *
 * @param {object}  opts
 * @param {object}  opts.user           the signed-in user
 * @param {string}  opts.role           their resolved role
 * @param {boolean} opts.policyAllows   `can(ctx, CC_REP_ASSIGN)`
 * @param {*}       opts.permissions    explicit grants from the backend
 * @param {object} [opts.overrides]     the local store (injected for tests)
 * @returns {{ allowed:boolean, source:string, why:string, persisted:boolean }}
 */
export function resolveManageAssignments({
  user,
  role,
  policyAllows = false,
  permissions = null,
  authoritativeAllowed = undefined,
  overrides = undefined,
} = {}) {
  if (typeof authoritativeAllowed === 'boolean') {
    return { allowed: authoritativeAllowed, source: 'backend', persisted: true,
      why: authoritativeAllowed ? '' : `${MANAGE_ASSIGNMENTS_LABEL} is not granted by the current server policy.` }
  }
  const store = overrides === undefined ? readOverrides() : overrides || {}
  const uKey = userSubject(user)
  const rKey = roleSubject(role)

  if (grantedByBackend(permissions)) {
    return { allowed: true, source: 'backend', persisted: true, why: '' }
  }
  if (uKey && Object.prototype.hasOwnProperty.call(store, uKey)) {
    const on = !!store[uKey]
    return {
      allowed: on,
      source: 'local-user',
      persisted: false,
      why: on ? '' : `${MANAGE_ASSIGNMENTS_LABEL} was switched off for you by an executive.`,
    }
  }
  if (rKey && Object.prototype.hasOwnProperty.call(store, rKey)) {
    const on = !!store[rKey]
    return {
      allowed: on,
      source: 'local-role',
      persisted: false,
      why: on ? '' : `${MANAGE_ASSIGNMENTS_LABEL} is switched off for ${ROLE_LABEL(role)}.`,
    }
  }
  if (policyAllows) {
    return { allowed: true, source: 'role', persisted: true, why: '' }
  }
  return {
    allowed: false,
    source: 'denied',
    persisted: true,
    why:
      role === ROLES.UNSCOPED
        ? `${MANAGE_ASSIGNMENTS_LABEL} needs a role on your session, or an executive switching it on for you.`
        : `${ROLE_LABEL(role)} can’t manage assignments. An executive can switch it on for you.`,
  }
}

/** How a resolved grant should be described in the toggle UI. */
export function sourceLabel(source) {
  switch (source) {
    case 'backend':
      return 'granted on the roster'
    case 'local-user':
      return 'this session only — not persisted'
    case 'local-role':
      return 'role override, this session only — not persisted'
    case 'role':
      return 'from their role'
    default:
      return 'not granted'
  }
}
